import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/')

// ── Scene background gradient ──────────────────────────────────────────────────
function makeBackgroundTexture() {
  const size = 1024
  const canvas = document.createElement('canvas')
  canvas.width  = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  // Base: very dark near-black navy
  ctx.fillStyle = '#06060f'
  ctx.fillRect(0, 0, size, size)

  // Top-right: deep indigo bleed
  const g1 = ctx.createRadialGradient(
    size * 0.88, size * 0.08, 0,
    size * 0.88, size * 0.08, size * 0.62
  )
  g1.addColorStop(0,   'rgba(55, 10, 130, 0.75)')
  g1.addColorStop(0.5, 'rgba(25,  5,  70, 0.35)')
  g1.addColorStop(1,   'rgba(0,   0,   0, 0)')
  ctx.fillStyle = g1
  ctx.fillRect(0, 0, size, size)

  // Bottom-left: midnight blue bleed
  const g2 = ctx.createRadialGradient(
    size * 0.08, size * 0.92, 0,
    size * 0.08, size * 0.92, size * 0.58
  )
  g2.addColorStop(0,   'rgba(10, 35, 120, 0.70)')
  g2.addColorStop(0.5, 'rgba(5,  18,  65, 0.30)')
  g2.addColorStop(1,   'rgba(0,   0,   0, 0)')
  ctx.fillStyle = g2
  ctx.fillRect(0, 0, size, size)

  // Centre: faint violet lift behind the hero object
  const g3 = ctx.createRadialGradient(
    size * 0.50, size * 0.52, 0,
    size * 0.50, size * 0.52, size * 0.38
  )
  g3.addColorStop(0,   'rgba(35, 10, 80, 0.50)')
  g3.addColorStop(1,   'rgba(0,   0,  0, 0)')
  ctx.fillStyle = g3
  ctx.fillRect(0, 0, size, size)

  // Top-left: subtle dark crimson trace
  const g4 = ctx.createRadialGradient(
    size * 0.04, size * 0.04, 0,
    size * 0.04, size * 0.04, size * 0.42
  )
  g4.addColorStop(0,   'rgba(80, 8, 30, 0.45)')
  g4.addColorStop(1,   'rgba(0,  0,  0, 0)')
  ctx.fillStyle = g4
  ctx.fillRect(0, 0, size, size)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// ── Lighting — matched to reference image ─────────────────────────────────────
// Reference: warm key from upper-left-front, soft cool fill from right,
// subtle rim from behind, generous ambient so shadows aren't crushed.
// Exported so main.js can ramp intensity with p_env_e
export let envShadowLight = null
// All global lights that contaminate the env model — muted during env section
export let globalLightsToMute = []

function addLights(scene, isMobile = false) {
  // Light theme: lifted ambient so the hero model reads clean, not murky
  const ambient = new THREE.AmbientLight('#e8e8f5', 0.35)
  scene.add(ambient)

  // ── Model 1 (fox mask, y ≈ 0) ────────────────────────────────────────────
  // Warm key from upper-left-front — main face illumination
  const key = new THREE.DirectionalLight('#ffe8cc', 1.7)
  key.position.set(-3, 3, 5)
  key.castShadow = true
  key.shadow.mapSize.width  = isMobile ? 256 : 1024
  key.shadow.mapSize.height = isMobile ? 256 : 1024
  key.shadow.camera.near   = 0.5
  key.shadow.camera.far    = 20
  key.shadow.camera.left   = -4
  key.shadow.camera.right  =  4
  key.shadow.camera.top    =  4
  key.shadow.camera.bottom = -4
  key.shadow.bias          = -0.0005
  scene.add(key)

  // Cyan/turquoise point light from the right — dramatic depth & falloff
  const sidePoint1 = new THREE.PointLight('#00bbff', 3.0, 14)
  sidePoint1.position.set(3.5, 0.5, 2.5)
  scene.add(sidePoint1)

  // Purple rim from behind — ethereal edge separation
  const rim = new THREE.DirectionalLight('#aa66ff', 1.3)
  rim.position.set(0, 1, -5)
  scene.add(rim)


  // ── Model 2 & 3 (sculptures, y ≈ -6) ────────────────────────────────────
  // Warm key from upper-left-front — mirrors model 1's main illumination
  const charWarmKey = new THREE.DirectionalLight('#ffe8cc', 2.2)
  charWarmKey.position.set(-3, 4, 5)
  scene.add(charWarmKey)

  // Cool-blue fill from the right — counterbalances warm key, adds depth
  const charKey = new THREE.DirectionalLight('#88ccff', 1.4)
  charKey.position.set(3, 2, 4)
  scene.add(charKey)

  // Cyan/turquoise point light from the right — dramatic falloff & depth
  const sidePoint2 = new THREE.PointLight('#00ccff', 8, 14)
  sidePoint2.position.set(3.5, -5.5, 2.5)
  scene.add(sidePoint2)

  // Warm point near model 2 — local fill so body doesn't read flat
  const charFill = new THREE.PointLight('#ff9966', 3.5, 10)
  charFill.position.set(-2, -5, 3)
  scene.add(charFill)

  // Back/Rim: purple from behind — matches model 1's rim language
  const charBack = new THREE.DirectionalLight('#aa66ff', 1.8)
  charBack.position.set(0, 1, -6)
  scene.add(charBack)

  // Low bounce: subtle upward fill so feet/legs aren't crushed black
  const charBounce = new THREE.DirectionalLight('#3355aa', 0.6)
  charBounce.position.set(0, -5, 2)
  scene.add(charBounce)

  // ── Model 4 (Avatar, y ≈ -6, x ≈ 3.4) ───────────────────────────────────
  const avatarRim = new THREE.PointLight('#00ccff', 9, 16)
  avatarRim.position.set(7.5, -5.5, 2.5)
  scene.add(avatarRim)

  const avatarFill = new THREE.PointLight('#dd88ff', 4, 12)
  avatarFill.position.set(1.0, -4.5, 3.5)
  scene.add(avatarFill)

  // ── Model 5 (AvatarF, y ≈ -6, x ≈ 5.1) ──────────────────────────────────
  const avatarFRim = new THREE.PointLight('#00ddff', 9, 16)
  avatarFRim.position.set(9.0, -5.5, 2.5)
  scene.add(avatarFRim)

  const avatarFFill = new THREE.PointLight('#ff88cc', 4, 12)
  avatarFFill.position.set(2.8, -4.5, 3.5)
  scene.add(avatarFFill)
  // ── Model 3 (Axolotls, y ≈ -6, x ≈ 4.0) — purple rim to match Guardians ───
  const axolotlRim = new THREE.PointLight('#aa66ff', 6, 12)
  axolotlRim.position.set(4.0, -4.8, -2.5)   // from behind, centred on model
  scene.add(axolotlRim)

  const axolotlFill = new THREE.PointLight('#cc88ff', 3, 10)
  axolotlFill.position.set(5.5, -4.5, 2.5)   // side fill to catch the edge
  scene.add(axolotlFill)

  // ── Animation model (Axie, y ≈ -9, x ≈ 0) ───────────────────────────────
  // Side key — left side only, not frontal, so front stays moody/dark
  const axieKey = new THREE.DirectionalLight('#ffddee', 0.7)
  axieKey.position.set(-6, -7, 1)   // hard left, barely grazing the front
  scene.add(axieKey)

  // ── Purple rim — the hero light, wraps around the back edge ──────────────
  const axiePurpleRim = new THREE.PointLight('#8833ff', 9, 15)
  axiePurpleRim.position.set(2, -8, -4)   // behind-right of model
  scene.add(axiePurpleRim)

  // Secondary purple rim — top-behind for the spine/ear glow
  const axiePurpleRim2 = new THREE.PointLight('#aa55ff', 5, 12)
  axiePurpleRim2.position.set(-1, -6.5, -4)
  scene.add(axiePurpleRim2)

  // Faint cyan edge — just a whisper of contrast against the purple
  const axieRim = new THREE.PointLight('#44ddff', 2.0, 12)
  axieRim.position.set(3.5, -9.5, -2)
  scene.add(axieRim)

  // Very soft front fill — keeps face readable without washing it out
  const axieFill = new THREE.PointLight('#ffbbee', 0.8, 10)
  axieFill.position.set(-1, -9, 5)
  scene.add(axieFill)

  // ── Collect global lights that need muting during env section ───────────
  globalLightsToMute = [
    { light: ambient,      base: ambient.intensity },
    { light: key,          base: key.intensity },
    { light: rim,          base: rim.intensity },
    { light: charWarmKey,  base: charWarmKey.intensity },
    { light: charKey,      base: charKey.intensity },
    { light: charBack,     base: charBack.intensity },
    { light: charBounce,   base: charBounce.intensity },
    { light: sidePoint1,   base: sidePoint1.intensity },
    { light: sidePoint2,   base: sidePoint2.intensity },
    { light: charFill,     base: charFill.intensity },
    { light: avatarRim,    base: avatarRim.intensity },
    { light: avatarFill,   base: avatarFill.intensity },
    { light: avatarFRim,   base: avatarFRim.intensity },
    { light: avatarFFill,  base: avatarFFill.intensity },
    { light: axolotlRim,   base: axolotlRim.intensity },
    { light: axolotlFill,  base: axolotlFill.intensity },
    { light: axieKey,      base: axieKey.intensity },
    { light: axiePurpleRim,  base: axiePurpleRim.intensity },
    { light: axiePurpleRim2, base: axiePurpleRim2.intensity },
    { light: axieRim,      base: axieRim.intensity },
    { light: axieFill,     base: axieFill.intensity },
  ]

  // ── Environments shadow light ───────────────────────────────────────────
  // A single warm DirectionalLight with shadow casting, aimed at the terrain.
  // Intensity is driven by main.js (0 when not in env section, ramps up with p_env_e)
  // so it never affects other models during their sections.
  envShadowLight = new THREE.DirectionalLight('#ffe0a0', 0)  // starts at 0
  envShadowLight.position.set(-10, 2.5, 8)  // upper-left-front = matches envMap sun direction
  envShadowLight.target.position.set(0, -6, 0)  // aimed at terrain centre
  envShadowLight.castShadow = true
  envShadowLight.shadow.mapSize.width  = isMobile ? 512 : 2048
  envShadowLight.shadow.mapSize.height = isMobile ? 512 : 2048
  envShadowLight.shadow.camera.near   = 0.5
  envShadowLight.shadow.camera.far    = 25
  envShadowLight.shadow.camera.left   = -10
  envShadowLight.shadow.camera.right  =  10
  envShadowLight.shadow.camera.top    =  10
  envShadowLight.shadow.camera.bottom = -10
  envShadowLight.shadow.bias = -0.001
  scene.add(envShadowLight)
  scene.add(envShadowLight.target)

  // Model 6 (hologram rig) is unlit — shader handles its own glow
}

// ── Loading screen helpers ────────────────────────────────────────────────────
const barFill  = document.getElementById('loading-bar-fill')
const barLabel = document.getElementById('loading-label')
const screen   = document.getElementById('loading-screen')

function setProgress(pct) {
  barFill.style.width = `${pct}%`
  barLabel.textContent = pct < 100 ? `${Math.round(pct)}%` : 'Ready'
}

function dismissLoader() {
  // Brief pause so the user sees 100% before fade
  setTimeout(() => screen.classList.add('hidden'), 400)
}

// ── GLB loader ────────────────────────────────────────────────────────────────
function loadGLB(scene, path, worldY = 0, targetSize = 2.2, onProgress, worldX = 0, alignBottom = false) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    loader.setDRACOLoader(dracoLoader)
    loader.load(
      path,
      (gltf) => {
        const model = gltf.scene

        const box    = new THREE.Box3().setFromObject(model)
        const size   = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())

        // alignBottom: scale by HEIGHT so every character stands the same tall,
        // then offset so the lowest vertex sits exactly at y=0 (shared floor).
        // Default: scale by max dimension (preserves original behaviour).
        const refDim = alignBottom ? size.y : Math.max(size.x, size.y, size.z)
        const scale  = targetSize / refDim

        model.scale.setScalar(scale)
        if (alignBottom) {
          model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale)
        } else {
          model.position.set(-center.x * scale, -center.y * scale, -center.z * scale)
        }

        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow    = true
            child.receiveShadow = true
            // Only compute normals if the GLB has none — preserve baked smoothing groups
            if (!child.geometry.attributes.normal) {
              child.geometry.computeVertexNormals()
            }
            const mats = Array.isArray(child.material) ? child.material : [child.material]
            mats.forEach(m => { m.needsUpdate = true })
          }
        })

        const pivot = new THREE.Group()
        pivot.add(model)
        pivot.position.set(worldX, worldY, 0)
        scene.add(pivot)
        resolve(pivot)
      },
      (xhr) => { if (xhr.total && onProgress) onProgress(xhr.loaded / xhr.total) },
      reject
    )
  })
}

// ── Animated GLB loader — returns pivot + raw animation clips ─────────────────
function loadAnimatedGLB(scene, path, worldY = 0, targetSize = 2.2, worldX = 0, alignBottom = false) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    loader.setDRACOLoader(dracoLoader)
    loader.load(
      path,
      (gltf) => {
        const model  = gltf.scene
        const box    = new THREE.Box3().setFromObject(model)
        const size   = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())

        const refDim = alignBottom ? size.y : Math.max(size.x, size.y, size.z)
        const scale  = targetSize / refDim
        model.scale.setScalar(scale)
        if (alignBottom) {
          model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale)
        } else {
          model.position.set(-center.x * scale, -center.y * scale, -center.z * scale)
        }

        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow    = true
            child.receiveShadow = true
            if (!child.geometry.attributes.normal) child.geometry.computeVertexNormals()
            const mats = Array.isArray(child.material) ? child.material : [child.material]
            mats.forEach(m => { m.needsUpdate = true })
          }
        })

        const pivot = new THREE.Group()
        pivot.add(model)
        pivot.position.set(worldX, worldY, 0)
        scene.add(pivot)
        resolve({ pivot, clips: gltf.animations })
      },
      undefined,
      reject
    )
  })
}

// Y level for the Environments section (same Y as characters — camera lifts above)
export const ENV_WORLD_Y  = -6
// Y level for the Animation showcase model
export const ANIM_WORLD_Y = -9

// ── Radial edge-fade texture for Plane.001 ────────────────────────────────────
// White centre → black edges → used as alphaMap so terrain fades out at borders.
function makeEdgeFadeTexture(size = 512) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx  = canvas.getContext('2d')
  const half = size / 2
  const grad = ctx.createRadialGradient(half, half, 0, half, half, half)
  grad.addColorStop(0.30, '#ffffff')   // fully opaque core
  grad.addColorStop(0.65, '#cccccc')   // gentle start of fade
  grad.addColorStop(0.88, '#555555')   // accelerating fade
  grad.addColorStop(1.00, '#000000')   // fully transparent edge
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(canvas)
  return tex
}

// ── Procedural directional environment map ───────────────────────────────────
// Generates a warm HDRI-like env map with a single "sun" light source.
// The sun position, brightness, colour and ambient level are all configurable
// so the GUI can regenerate the map in real time.
// Applied per-material via material.envMap — does NOT touch scene.environment.

let _cachedRenderer = null

export function generateEnvMap(renderer, opts = {}) {
  _cachedRenderer = renderer || _cachedRenderer
  const r = _cachedRenderer
  const {
    sunX        = 0.18,   // 0–1 horizontal position on equirect (0=left, 1=right)
    sunY        = 0.32,   // 0–1 vertical position (0=top, 1=bottom)
    sunRadius   = 0.30,   // size of the sun glow (fraction of width)
    sunBright   = 1.0,    // 0–2 overall sun brightness multiplier
    sunR        = 255,    // sun colour RGB
    sunG        = 230,
    sunB        = 160,
    ambientR    = 8,      // base ambient RGB (shadow colour)
    ambientG    = 8,
    ambientB    = 16,
    groundBounce = 0.25,  // warm ground fill strength
    skyFill     = 0.15,   // cool sky fill on shadow side
  } = opts

  const pmrem = new THREE.PMREMGenerator(r)
  pmrem.compileCubemapShader()

  const w = 512, h = 256
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  const ctx = canvas.getContext('2d')

  // Base ambient
  ctx.fillStyle = `rgb(${ambientR},${ambientG},${ambientB})`
  ctx.fillRect(0, 0, w, h)

  // Ground bounce
  const ground = ctx.createLinearGradient(0, h * 0.6, 0, h)
  ground.addColorStop(0.0, 'rgba(0,0,0,0)')
  ground.addColorStop(0.5, `rgba(100,60,30,${groundBounce})`)
  ground.addColorStop(1.0, `rgba(60,35,15,${groundBounce * 0.6})`)
  ctx.fillStyle = ground
  ctx.fillRect(0, 0, w, h)

  // Sun glow
  const sx = w * sunX, sy = h * sunY, sr = w * sunRadius
  const sun = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr)
  sun.addColorStop(0.0,  `rgba(${sunR},${sunG},${sunB},${sunBright})`)
  sun.addColorStop(0.15, `rgba(${sunR},${Math.round(sunG*0.87)},${Math.round(sunB*0.75)},${sunBright*0.85})`)
  sun.addColorStop(0.4,  `rgba(${Math.round(sunR*0.86)},${Math.round(sunG*0.65)},${Math.round(sunB*0.38)},${sunBright*0.35})`)
  sun.addColorStop(0.7,  `rgba(${Math.round(sunR*0.39)},${Math.round(sunG*0.26)},${Math.round(sunB*0.13)},${sunBright*0.08})`)
  sun.addColorStop(1.0,  'rgba(0,0,0,0)')
  ctx.fillStyle = sun
  ctx.fillRect(0, 0, w, h)

  // Cool sky fill opposite the sun
  const oppX = w * (1 - sunX), oppY = h * 0.35
  const sky = ctx.createRadialGradient(oppX, oppY, 0, oppX, oppY, w * 0.35)
  sky.addColorStop(0.0, `rgba(60,70,120,${skyFill})`)
  sky.addColorStop(1.0, 'rgba(0,0,0,0)')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, w, h)

  const tex = new THREE.CanvasTexture(canvas)
  tex.mapping = THREE.EquirectangularReflectionMapping
  tex.colorSpace = THREE.SRGBColorSpace

  const envMap = pmrem.fromEquirectangular(tex).texture
  tex.dispose()
  pmrem.dispose()
  return envMap
}

// Initial load helper (returns a promise for backward compat)
let _envMapPromise = null
export function getEnvMap(renderer) {
  if (_envMapPromise) return _envMapPromise
  _envMapPromise = Promise.resolve(generateEnvMap(renderer))
  return _envMapPromise
}

// ── Environment GLB loader — applies radial fade to Plane.001 ────────────────
function loadEnvGLB(scene, path, worldY = 0, targetSize = 6) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    loader.setDRACOLoader(dracoLoader)
    loader.load(
      path,
      (gltf) => {
        const model  = gltf.scene
        const box    = new THREE.Box3().setFromObject(model)
        const size   = box.getSize(new THREE.Vector3())
        const scale  = targetSize / Math.max(size.x, size.y, size.z)

        model.scale.setScalar(scale)

        // Find Plane.001 centre and use it as rotation pivot
        let planeCenter = null
        model.traverse(child => {
          if (child.isMesh && child.name === 'Plane.001') {
            child.geometry.computeBoundingBox()
            planeCenter = new THREE.Vector3()
            child.geometry.boundingBox.getCenter(planeCenter)
            // Transform to world space of the unscaled model
            child.localToWorld(planeCenter)
          }
        })
        // Fallback to bounding box centre if Plane.001 not found
        if (!planeCenter) {
          planeCenter = new THREE.Vector3()
          box.getCenter(planeCenter)
        }
        model.position.set(-planeCenter.x * scale, -planeCenter.y * scale, -planeCenter.z * scale)

        const fadeTex = makeEdgeFadeTexture()

        model.traverse(child => {
          if (!child.isMesh) return
          child.castShadow    = true    // buildings cast shadows on each other
          child.receiveShadow = true
          if (!child.geometry.attributes.normal) child.geometry.computeVertexNormals()

          const mats = Array.isArray(child.material) ? child.material : [child.material]

          if (child.name === 'Plane.001') {
            // Radial edge-fade: transparent at borders, opaque in the middle
            child.receiveShadow = true
            mats.forEach(m => {
              m.transparent     = true
              m.alphaMap        = fadeTex
              m.alphaTest       = 0
              m.depthWrite      = false
              m.roughness       = 1.0
              m.metalness       = 0.0
              m.envMapIntensity = 4.0
              m.needsUpdate     = true
            })
          } else {
            // All env materials — MeshStandardMaterial with PBR shading.
            // The procedural envMap is applied later in main.js once the renderer
            // is available. That envMap lights the scene instead of point lights.
            mats.forEach(m => {
              const hasAlphaMap = m.alphaTest > 0
              const mapHasAlpha = m.map && (m.map.format === THREE.RGBAFormat)
              const isLeaf      = hasAlphaMap || mapHasAlpha
              if (isLeaf) {
                if (m.alphaTest === 0) m.alphaTest = 0.5
                m.side        = THREE.DoubleSide
                m.transparent = false
                m.depthWrite  = true
                m._isLeaf     = true
              }
              m.roughness      = 1.0    // fully diffuse for sandy/matte surfaces
              m.metalness      = 0.0
              m.envMapIntensity = 6.6   // strong envMap to dominate over flat global lights
              m.needsUpdate    = true
            })
          }
        })

        const pivot = new THREE.Group()
        pivot.add(model)
        pivot.position.set(0, worldY, 0)
        scene.add(pivot)
        resolve(pivot)
      },
      undefined,
      reject
    )
  })
}

// ── Public API ────────────────────────────────────────────────────────────────
export async function buildScene(scene, isMobile = false) {
  scene.fog = new THREE.FogExp2(new THREE.Color('#efe6ec'), 0.018)
  addLights(scene, isMobile)

  // Model 1 — hero character, 3 skin variants
  const model1Promise     = loadGLB(scene, '/assets/Ukazoire_Purple.glb', 0, 2.2, p => setProgress(p * 100))
  const heroYellowPromise = loadGLB(scene, '/assets/Ukazoire_Yellow.glb', 0, 2.2)
  const heroRedPromise    = loadGLB(scene, '/assets/Ukazoire_Red.glb',    0, 2.2)

  // Carousel order: Avatar/male (0) → AvatarF/female (1) → LG1 (2) → sculpture (3)
  // alignBottom=true so all characters share the same floor level
  const SECTION_GAP   = 6
  const CAROUSEL_GAP  = 1.3

  // Carousel X positions — 1.5 spacing, 4 slides
  const model6Promise = loadGLB(scene, '/assets/FemaleandMaleAvatar_01.glb', -SECTION_GAP - 0.50, 2.2, undefined, -0.20, true)  // slide 1 (duo)
  const model4Promise = loadGLB(scene, '/assets/MaleAndFemaleGuardians.glb', -SECTION_GAP + 0.05, 2.7, undefined,  2.15, true)  // slide 2 (Guardians)
  const model3Promise = loadGLB(scene, '/assets/AxolotlsAvtatrs.glb',        -SECTION_GAP + 0.00, 1.8, undefined,  4.25, true)  // slide 3

  // Animation showcase model — centred, between characters and VFX
  const animModelPromise = loadAnimatedGLB(scene, '/assets/Axie_Anim2.glb', ANIM_WORLD_Y, 2.2, 0.0, true)

  // Environments: Tattoine terrain, same Y level as characters (camera lifts above for top-down)
  const envModelPromise = loadEnvGLB(scene, '/assets/Tattoine.glb', ENV_WORLD_Y, 16)

  // LG skinned models — 3 colour variants shown in the "Built Different" about section
  const lgPurplePromise = loadGLB(scene, '/assets/LG_Purple.glb.glb', 0, 2.2)
  const lgYellowPromise = loadGLB(scene, '/assets/LG_Yellow.glb.glb', 0, 2.2)
  const lgGreenPromise  = loadGLB(scene, '/assets/LG_Green.glb.glb',  0, 2.2)

  const [model1, model3, model4, model6, animResult, envModel, lgPurple, lgYellow, lgGreen, heroYellow, heroRed] = await Promise.all([
    model1Promise, model3Promise, model4Promise, model6Promise, animModelPromise, envModelPromise,
    lgPurplePromise, lgYellowPromise, lgGreenPromise,
    heroYellowPromise, heroRedPromise,
  ])
  const model2 = null  // removed
  const model5 = null  // removed

  setProgress(100)
  dismissLoader()

  return {
    model1, model2: null, model3, model4, model5: null, model6,
    animModel: animResult.pivot, animClips: animResult.clips,
    envModel,
    lgPurple, lgYellow, lgGreen,
    heroYellow, heroRed,
    sectionGap: SECTION_GAP, carouselGap: CAROUSEL_GAP
  }
}
